import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { DIMENSIONS, categoryColor, VERSO_URL } from "@/data/constants";
import { SectionHead } from "@/pages/Home";
import { ArrowRight, RotateCcw } from "lucide-react";

const CAT_BY_KEY = { corps: "CORPS", coeur: "CŒUR", cerveau: "CERVEAU", conscience: "CONSCIENCE" };
const MAX_DRAWS = 3;

const PRICING = [
  { slug: "gratuit", price: "Gratuit", title: "Carte découverte", desc: "Expérience limitée pour goûter à la méthode.", cta: "ESSAYER", to: "/jeu", highlight: false },
  { slug: "num21", price: "9,90 €", title: "Parcours numérique 21 jours", desc: "Le parcours complet des 21 jours en ligne.", cta: "COMMENCER", to: "/carnets", highlight: true },
  { slug: "num21pdf", price: "14,90 €", title: "Parcours + carnet PDF", desc: "Le parcours numérique accompagné du carnet en PDF.", cta: "DÉCOUVRIR", to: "/carnets", highlight: false },
  { slug: "collection", price: "79 €", title: "Collection numérique", desc: "Les 10 parcours de la collection.", cta: "VOIR LA COLLECTION", to: "/carnets", highlight: false },
];

export default function Jeu() {
  const [cards, setCards] = useState([]);
  const [drawn, setDrawn] = useState({}); // key -> card
  const [flipped, setFlipped] = useState({});
  const [draws, setDraws] = useState(0);
  const [activeCarnet, setActiveCarnet] = useState(null);

  useEffect(() => {
    api.get("/game/cards").then((r) => setCards(r.data));
    api.get("/books").then((r) => setActiveCarnet(r.data.find((b) => b.number >= 1 && b.active) || null));
    setDraws(Number(sessionStorage.getItem("tao_draws") || 0));
  }, []);

  const drawnCount = Object.keys(drawn).length;
  const reachedLimit = draws >= MAX_DRAWS;
  const allDrawn = drawnCount === DIMENSIONS.length;

  const drawCard = (dimKey) => {
    if (drawn[dimKey] || reachedLimit) return;
    const cat = CAT_BY_KEY[dimKey];
    const pool = cards.filter((c) => c.category === cat);
    if (pool.length === 0) return;
    const card = pool[Math.floor(Math.random() * pool.length)];
    setDrawn((d) => ({ ...d, [dimKey]: card }));
    setFlipped((f) => ({ ...f, [dimKey]: true }));
    const next = draws + 1;
    setDraws(next);
    sessionStorage.setItem("tao_draws", String(next));
  };

  return (
    <div>
      <section className="bg-[#1F2A24] text-[#F6F2E9]">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 py-16 md:py-24 text-center">
          <p className="font-sans text-xs uppercase tracking-[0.25em] text-[#C9A24B] font-semibold">Le Tao des 4C</p>
          <h1 className="font-serif text-4xl md:text-6xl mt-3">Le Jeu des 21 Jours</h1>
          <p className="font-sans text-sm md:text-base text-[#F6F2E9]/70 mt-4">
            Un jour. Une carte. Une prise de conscience. Une action.
          </p>
          <div className="gold-divider w-24 mx-auto my-8" />
          <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#C9A24B] font-semibold">
            J'ai rendez-vous avec mon futur
          </p>
          <h2 className="font-serif text-2xl md:text-4xl mt-2">Dans 21 jours</h2>
          <p className="font-serif italic text-base md:text-lg text-[#F6F2E9]/80 mt-6 max-w-2xl mx-auto leading-relaxed">
            « Le premier jour, tu enregistres un message à la personne que tu seras dans 21 jours.
            Pendant 21 jours, tu avances avec les cartes du Tao des 4C. Au dernier jour, tu retrouves ton message.
            Ton futur te parle. Puis tu lui réponds. »
          </p>
          {activeCarnet && (
            <Link
              to={`/carnet/${activeCarnet.id}`}
              data-testid="jeu-start-parcours"
              className="inline-flex items-center gap-2 rounded-full bg-[#C9A24B] text-[#1F2A24] px-8 py-4 font-ui text-sm tracking-wide mt-8 hover:bg-[#b8923f] transition-colors"
            >
              COMMENCER LE PARCOURS <ArrowRight size={18} />
            </Link>
          )}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-8 pt-12 md:pt-16">
        <Link
          to="/jeu/ile"
          data-testid="jeu-ile-banner"
          className="group relative block overflow-hidden rounded-3xl border border-[#C9A24B]/30 bg-[#1F2A24] p-8 md:p-10"
        >
          <div className="absolute inset-0 opacity-15" style={{ backgroundImage: `url(${VERSO_URL})`, backgroundSize: "cover", backgroundPosition: "center" }} />
          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#C9A24B] font-semibold">Nouveau — Le Jeu numérique</p>
              <h3 className="font-serif text-2xl md:text-3xl text-[#F6F2E9] mt-2">L'Île de l'Abondance · 21 cartes</h3>
              <p className="font-sans text-sm text-[#F6F2E9]/70 mt-2 max-w-xl">
                Un voyage illustré en 21 cartes. 3 cartes offertes, la collection complète en premium, et le tirage libre une fois débloqué.
              </p>
            </div>
            <span className="inline-flex items-center gap-2 rounded-full bg-[#C9A24B] text-[#1F2A24] px-6 py-3 font-ui text-sm tracking-wide whitespace-nowrap group-hover:bg-[#b8923f] transition-colors">
              OUVRIR LE JEU <ArrowRight size={18} />
            </span>
          </div>
        </Link>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-12 md:py-16">
        <SectionHead eyebrow="Gratuit — carte découverte" title="Essayez le jeu" center />
        <p data-testid="draws-counter" className="text-center font-sans text-sm text-[#1F2A24]/60 mt-4">
          Tirages restants : {Math.max(0, MAX_DRAWS - draws)} / {MAX_DRAWS}
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10" data-testid="game-deck">
          {DIMENSIONS.map((dim) => {
            const card = drawn[dim.key];
            const color = dim.color;
            return (
              <div
                key={dim.key}
                data-testid={`game-card-${dim.key}`}
                className={`flip-card ${flipped[dim.key] ? "is-flipped" : ""} h-80`}
                onClick={() => (card ? null : drawCard(dim.key))}
              >
                <div className="flip-inner">
                  <div className="flip-face flex flex-col items-center justify-center text-center overflow-hidden">
                    <img src={VERSO_URL} alt="Dos de carte" className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 flex flex-col items-center justify-between p-4 bg-gradient-to-t from-black/45 via-transparent to-black/25">
                      <span className="font-serif text-xl text-white drop-shadow mt-2">{dim.label}</span>
                      {!card && !reachedLimit && (
                        <span className="rounded-full bg-white/25 backdrop-blur px-4 py-2 font-ui text-xs tracking-wide text-white inline-flex items-center gap-2">
                          <RotateCcw size={13} /> TIRER UNE CARTE
                        </span>
                      )}
                      {!card && reachedLimit && (
                        <span className="rounded-full bg-black/40 px-3 py-1.5 font-ui text-xs text-white/90">Limite atteinte</span>
                      )}
                    </div>
                  </div>
                  <div className="flip-face flip-back bg-white border p-5 flex flex-col" style={{ borderColor: color }}>
                    {card && (
                      <>
                        <span className="font-ui text-[11px] tracking-widest" style={{ color }}>
                          {card.category}
                        </span>
                        <h3 className="font-serif text-lg text-[#1F2A24] mt-1">{card.title}</h3>
                        <p className="font-serif italic text-sm text-[#1F2A24]/80 mt-2">{card.question}</p>
                        <p className="font-sans text-xs text-[#1F2A24]/70 mt-3">
                          <b style={{ color }}>Expérience — </b>{card.exercise}
                        </p>
                        <p className="font-sans text-xs text-[#1F2A24]/70 mt-2">
                          <b style={{ color }}>Action — </b>{card.action}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {(allDrawn || reachedLimit) && (
          <div data-testid="game-end-message" className="mt-12 text-center rounded-2xl bg-[#1F2A24] text-[#F6F2E9] p-8 md:p-10">
            <p className="font-serif text-xl md:text-2xl">
              {allDrawn
                ? "Vous venez d'expérimenter les 4 C."
                : "Vous avez découvert les 4 C. Pour aller plus loin, explorez les carnets de 21 jours."}
            </p>
            <Link
              to="/carnets"
              data-testid="game-discover-carnets"
              className="inline-flex items-center gap-2 rounded-full bg-[#C9A24B] text-[#1F2A24] px-7 py-3.5 font-ui text-sm tracking-wide mt-6 hover:bg-[#b8923f] transition-colors"
            >
              DÉCOUVRIR LES CARNETS <ArrowRight size={18} />
            </Link>
          </div>
        )}
      </section>

      <section className="bg-[#F8F5EE] border-t border-[#E8DCC4]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-16 md:py-20">
          <SectionHead eyebrow="Accéder au parcours" title="Choisir sa formule" center />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10" data-testid="pricing-grid">
            {PRICING.map((p) => (
              <div
                key={p.slug}
                data-testid={`price-${p.slug}`}
                className={`rounded-2xl border p-6 flex flex-col bg-white ${p.highlight ? "border-[#C9A24B] shadow-md" : "border-[#E8DCC4]"}`}
              >
                {p.highlight && (
                  <span className="self-start rounded-full bg-[#C9A24B]/15 text-[#C9A24B] px-3 py-1 font-ui text-[10px] tracking-wider mb-3">
                    RECOMMANDÉ
                  </span>
                )}
                <span className="font-serif text-3xl text-[#385E3A]">{p.price}</span>
                <h3 className="font-serif text-lg text-[#1F2A24] mt-2">{p.title}</h3>
                <p className="font-sans text-sm text-[#1F2A24]/70 mt-2 flex-1">{p.desc}</p>
                <Link
                  to={p.to}
                  className={`mt-5 text-center rounded-full px-5 py-2.5 font-ui text-sm transition-colors ${
                    p.highlight ? "bg-[#C9A24B] text-[#1F2A24] hover:bg-[#b8923f]" : "bg-[#385E3A] text-white hover:bg-[#2c4a2e]"
                  }`}
                >
                  {p.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
